import React, { useEffect, useState, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';
import 'leaflet/dist/leaflet.css';

// Fix default marker icons (webpack breaks the default icon paths)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const STATUS_LABELS = {
  active: '公開中',
  pending: '申請中',
  archived: 'アーカイブ',
};

const STATUS_COLORS = {
  active: '#198754',
  pending: '#ffc107',
  archived: '#6c757d',
};

function createStatusIcon(status) {
  const color = STATUS_COLORS[status] || '#0d6efd';
  return L.divIcon({
    className: '',
    html: `<div style="
      width:12px;height:12px;border-radius:50%;
      background:${color};border:2px solid #fff;
      box-shadow:0 1px 3px rgba(0,0,0,0.4);
    "></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
    popupAnchor: [0, -8],
  });
}

function MapEventHandler({ onMoveEnd }) {
  useMapEvents({
    moveend: (e) => {
      const map = e.target;
      const bounds = map.getBounds();
      onMoveEnd(bounds);
    },
  });
  return null;
}

export default function StoreMap({ status, queryKeyword, prefecture, storeType }) {
  const [allStores, setAllStores] = useState([]);
  const [visibleStores, setVisibleStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fetchRef = useRef(0);
  const boundsRef = useRef(null);

  // Fetch all stores from admin API (paginated, accumulate all pages)
  const fetchAllStores = useCallback(async () => {
    const id = ++fetchRef.current;
    setLoading(true);
    setError('');
    try {
      let accumulated = [];
      let cursor = '';
      let hasMore = true;

      while (hasMore) {
        const url = new URL(`${API_BASE_URL}/admin/stores`);
        url.searchParams.set('limit', '200');
        if (cursor) url.searchParams.set('cursor', cursor);
        if (status) url.searchParams.set('status', status);
        if (queryKeyword) url.searchParams.set('q', queryKeyword);
        if (prefecture) url.searchParams.set('prefecture', prefecture);
        if (storeType) url.searchParams.set('store_type', storeType);

        const res = await apiFetch(url.toString());
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (id !== fetchRef.current) return; // stale

        const list = data.data?.items || data.items || data.data || [];
        accumulated = accumulated.concat(Array.isArray(list) ? list : []);

        cursor = data.meta?.cursor || '';
        hasMore = !!(data.data?.has_more ?? data.has_more ?? data.meta?.hasMore);
        if (!cursor) hasMore = false;
      }

      if (id !== fetchRef.current) return;

      // Filter to stores that have coordinates
      const withCoords = accumulated.filter((s) => s.lat && s.lng);
      setAllStores(withCoords);

      // Apply current bounds filter
      if (boundsRef.current) {
        setVisibleStores(filterByBounds(withCoords, boundsRef.current));
      } else {
        setVisibleStores(withCoords);
      }
    } catch (e) {
      if (id === fetchRef.current) {
        setError(e.message || 'Network Error');
      }
    } finally {
      if (id === fetchRef.current) {
        setLoading(false);
      }
    }
  }, [status, prefecture, storeType, queryKeyword]);

  useEffect(() => {
    fetchAllStores();
  }, [fetchAllStores]);

  const handleMoveEnd = useCallback(
    (bounds) => {
      boundsRef.current = bounds;
      setVisibleStores(filterByBounds(allStores, bounds));
    },
    [allStores]
  );

  return (
    <div className="card">
      <div className="card-body p-0" style={{ position: 'relative' }}>
        {loading && (
          <div
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 1000,
              background: 'rgba(255,255,255,0.9)',
              padding: '4px 12px',
              borderRadius: 4,
              fontSize: '0.75rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }}
          >
            読み込み中...
          </div>
        )}
        {error && (
          <div
            style={{
              position: 'absolute',
              top: 10,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              background: '#f8d7da',
              color: '#842029',
              padding: '4px 12px',
              borderRadius: 4,
              fontSize: '0.75rem',
            }}
          >
            {error}
          </div>
        )}
        <MapContainer
          center={[36.5, 138]}
          zoom={6}
          style={{ height: '65vh', width: '100%', borderRadius: '0.375rem' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          />
          <MapEventHandler onMoveEnd={handleMoveEnd} />
          {visibleStores.map((store) => (
            <Marker
              key={store.id}
              position={[store.lat, store.lng]}
              icon={createStatusIcon(store.status)}
            >
              <Popup>
                <div style={{ minWidth: 180, fontSize: '0.8125rem' }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{store.name}</div>
                  <div style={{ color: '#6c757d', marginBottom: 4 }}>
                    {store.address || '-'}
                  </div>
                  <div style={{ marginBottom: 4 }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '1px 6px',
                        borderRadius: 4,
                        fontSize: '0.6875rem',
                        color: '#fff',
                        background: STATUS_COLORS[store.status] || '#6c757d',
                      }}
                    >
                      {STATUS_LABELS[store.status] || store.status || '-'}
                    </span>
                    {store.machine_count != null && (
                      <span style={{ marginLeft: 8, fontSize: '0.75rem', color: '#6c757d' }}>
                        {store.machine_count}台
                      </span>
                    )}
                  </div>
                  <Link
                    to={`/stores/${store.id}`}
                    style={{ fontSize: '0.75rem' }}
                  >
                    詳細を見る
                  </Link>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      <div className="card-footer bg-white d-flex justify-content-between align-items-center">
        <span style={{ fontSize: '0.8125rem', color: '#6c757d' }}>
          {visibleStores.length} 件表示 / 全 {allStores.length} 件
        </span>
        <div className="d-flex gap-2 align-items-center" style={{ fontSize: '0.75rem' }}>
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <span key={key} className="d-flex align-items-center gap-1">
              <span
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: STATUS_COLORS[key],
                }}
              />
              {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function filterByBounds(stores, bounds) {
  return stores.filter((s) => bounds.contains([s.lat, s.lng]));
}
